import {Howl, Howler} from "howler";
import "howler-plugin-reverb/";
import irURL from "./ir.wav";
//import AudioPlayer from "./audioplayer";

type Chunk = {audioStream:string, duration:number, chunks:any[]};

const voiceParams = {
  "hu": {
    "name": "Noemi",
    "engine": "azure",
    "languageCode": "hu-HU"
  },
  "en": {
    "name": "Sonia",
    "engine": "azure",
    "languageCode": "en-GB"
  }
  /*"en": {
    "name": "Joanna",
    "engine": "neural",
    "languageCode": "en-US"
  }*/
}

const MAX_RETRIES = 2;
const RETRY_DELAY = 1000;
const MAX_CONSECUTIVE_ERRORS = 3;

class Reader
{
  index = 0;
  sentences:string[] = [];
  chunkMap: Map<number, Chunk> = new Map();
  player?:Howl;
  background?:Howl;
  playing = false;
  paused = false;
  nextTimer?:NodeJS.Timeout;
  safetyTimer?:NodeJS.Timeout;
  pendingLoad?:{index:number, promise:Promise<Chunk>};
  playGeneration:number = 0; // guards against concurrent play() calls
  lang:string = "en";
  inited =false;
  listener?:(sentence:SpeechifyChunk[]) => void;
  rate:number = 0.85;
  onPlaybackEnd?:() => void;
  onIndexChange?:(index:number, total:number, sentence:string) => void;
  backgroundMusicEnabled:boolean = true;
  consecutiveErrors:number = 0;

  constructor(public onSentenceChange?:(sentence:string, index:number) => void) {}

  // Legacy getter for compatibility (seekToIndex, etc.)
  get chunks(): Chunk[] {
    const arr: Chunk[] = [];
    for (let i = 0; i < this.sentences.length; i++) {
      arr[i] = this.chunkMap.get(i) || {audioStream:"", duration:0, chunks:[]};
    }
    return arr;
  }

  async play(sentences:string[], lang:string="en")
  {
    // Stop any existing playback cleanly
    this.cleanupPlayer();
    if (this.nextTimer) clearTimeout(this.nextTimer);
    if (this.safetyTimer) clearTimeout(this.safetyTimer);

    this.playing = true;
    this.paused = false;
    this.consecutiveErrors = 0;
    const gen = ++this.playGeneration; // guard: if play() is called again, this generation becomes stale

    this.lang = lang;

    if (!this.inited)
    {
      Howler.usingWebAudio = true;
      Howler.autoUnlock = true;
      await Howler.loadIR("ir", irURL);
      this.inited = true;
    }

    // Resume AudioContext if suspended (common on mobile after tab switch)
    if (Howler.ctx && Howler.ctx.state === 'suspended')
      Howler.ctx.resume();

    if (!this.background)
    {
      this.background = new Howl({
        src: ['/music/background1.mp3'],
        html5: true,
        volume: 0.2,
        loop: true
      });
    }

    if (!this.playing || gen !== this.playGeneration) return;

    if (this.backgroundMusicEnabled)
    {
      this.background?.play();
      this.background.once('play', () =>{
        this.background?.fade(0,0.2, 3);
        let dur = this.background?.duration();
        if (dur && dur > 0)
          this.background!.seek(Math.round(dur * 0.8 * Math.random()));
      });
    }

    this.sentences = sentences;
    this.chunkMap = new Map();
    this.index = 0;

    // Load first chunk
    let firstChunk = await this.getChunkWithRetry(this.sentences[0]);
    if (!this.playing || gen !== this.playGeneration) return;
    this.chunkMap.set(0, firstChunk);

    this.playChunk();
  }

  pause()
  {
    //console.log("pause");
    if (this.player)
    {
      if (this.background?.playing())
        this.background?.pause();

      if (this.player?.playing())
        this.player?.pause();
    }
    this.playing = false;
    this.paused = true;
  }

  resume()
  {
    if (this.paused && this.player)
    {
      // Resume AudioContext if suspended (mobile tab switch, etc.)
      if (Howler.ctx && Howler.ctx.state === 'suspended')
        Howler.ctx.resume();

      this.playing = true;
      this.paused = false;
      this.player.play();
      if (this.backgroundMusicEnabled && this.background)
        this.background.play();
    }
  }

  stop()
  {
    this.playing = false;
    this.paused = false;
    this.playGeneration++;
    if (this.nextTimer) clearTimeout(this.nextTimer);
    if (this.safetyTimer) clearTimeout(this.safetyTimer);
    this.cleanupPlayer();
    if (this.background) {
      this.background.fade(0.2, 0, 1000);
      setTimeout(() => { this.background?.stop(); }, 1000);
    }
    this.index = 0;
    this.sentences = [];
    this.chunkMap = new Map();
    this.pendingLoad = undefined;
  }

  private cleanupPlayer()
  {
    if (this.player)
    {
      this.player.off(); // remove ALL event listeners to prevent ghost callbacks
      this.player.stop();
      this.player.unload();
      this.player = undefined;
    }
  }

  setRate(newRate:number)
  {
    this.rate = newRate;
    if (this.player) this.player.rate(newRate);
  }

  setVolume(volume:number)
  {
    Howler.volume(volume);
  }

  mute()
  {
    Howler.mute(true);
  }

  unmute()
  {
    Howler.mute(false);
  }

  toggleBackground()
  {
    this.backgroundMusicEnabled = !this.backgroundMusicEnabled;
    if (!this.backgroundMusicEnabled)
    {
      if (this.background?.playing())
        this.background.pause();
    }
    else
    {
      if (this.playing && this.background && !this.background.playing())
        this.background.play();
    }
  }

  async seekToIndex(targetIndex:number)
  {
    if (targetIndex < 0 || targetIndex >= this.sentences.length)
      return;

    this.cleanupPlayer();
    if (this.nextTimer) clearTimeout(this.nextTimer);
    if (this.safetyTimer) clearTimeout(this.safetyTimer);
    this.consecutiveErrors = 0;

    // If the chunk is already loaded, just play it
    if (this.chunkMap.has(targetIndex))
    {
      this.index = targetIndex;
      this.playing = true;
      this.paused = false;
      this.playChunk();
      return;
    }

    // Otherwise load it on the fly
    this.playing = true;
    this.paused = false;
    let chunk = await this.getChunkWithRetry(this.sentences[targetIndex]);
    this.chunkMap.set(targetIndex, chunk);
    this.index = targetIndex;
    this.playChunk();
  }

  setListener(listener:(sentence:SpeechifyChunk[]) => void)
  {
    this.listener = listener;
  }

  // Fix #3: retry logic with backoff
  private async getChunkWithRetry(text:string, retries:number = MAX_RETRIES):Promise<Chunk>
  {
    for (let attempt = 0; attempt <= retries; attempt++)
    {
      let chunk = await this.getChunk(text);
      if (chunk.chunks.length > 0)
      {
        this.consecutiveErrors = 0;
        return chunk;
      }

      // Failed — retry after delay if we have retries left
      if (attempt < retries)
      {
        console.warn(`Chunk load failed, retrying (${attempt + 1}/${retries})...`);
        await new Promise(r => setTimeout(r, RETRY_DELAY * (attempt + 1)));
        if (!this.playing) return chunk; // bail if stopped during retry
      }
    }

    // All retries exhausted
    console.warn("All retries exhausted for chunk");
    return {audioStream:"", duration:1, chunks: []};
  }

  private async getChunk(text:string)
  {
    //console.log("load...", text);
    let request = {
        "audioFormat": "ogg",
        "paragraphChunks": [
            text
        ],
        "voiceParams": voiceParams[this.lang] ? voiceParams[this.lang] : voiceParams["en"]
      };

      try {
        let data = await fetch("https://audio.api.speechify.dev/generateAudioFiles", {
          method: "POST",
          headers: {
            'Accept': '*/*',
            'Content-Type': 'application/json',
            "x-speechify-client": "API"
          },
          body: JSON.stringify(request)
        });

        if (!data.ok)
        {
          console.warn("Speechify API error:", data.status, data.statusText);
          return {audioStream:"", duration:1, chunks: []};
        }

        let result:SpeechifyResult = await data.json();

        let audioStream = result.audioStream;
        console.warn(result);
        let duration:number = result.speechMarks.chunks.reduce((prevV:number, newV:{endTime:number}) => prevV + newV.endTime, 0);
        return {audioStream:audioStream, duration: duration, chunks:result.speechMarks.chunks};
      }
      catch (e)
      {
        console.warn("Error loading chunk", e);
        return {audioStream:"", duration:1, chunks: []};
      }

  }

  private async playChunk()
  {
    if ( !this.playing )
      return;

    // Resume AudioContext if suspended (mobile tab switch, etc.)
    if (Howler.ctx && Howler.ctx.state === 'suspended')
      Howler.ctx.resume();

    let currentChunk = this.chunkMap.get(this.index);

    if ( !currentChunk || currentChunk.chunks.length == 0)
    {
      this.consecutiveErrors++;

      if (this.consecutiveErrors >= MAX_CONSECUTIVE_ERRORS)
      {
        console.warn(`${MAX_CONSECUTIVE_ERRORS} consecutive chunk errors, stopping playback`);
        this.playing = false;
        if (this.onPlaybackEnd)
          this.onPlaybackEnd();
        return;
      }

      // Skip to next, preloading it first
      if (this.index < this.sentences.length - 1)
      {
        this.index++;
        await this.ensureChunkLoaded(this.index);
        if (this.playing) this.playChunk();
      }
      return;
    }

    this.consecutiveErrors = 0;

    if (this.onSentenceChange)
      this.onSentenceChange(this.sentences[this.index], this.index);

    if (this.onIndexChange)
      this.onIndexChange(this.index, this.sentences.length, this.sentences[this.index]);

    if (this.listener)
      this.listener(currentChunk.chunks)

    // Clean up previous player to prevent ghost event listeners
    this.cleanupPlayer();
    if (this.safetyTimer) clearTimeout(this.safetyTimer);

    this.player = new Howl({src:["data:audio/wav;base64," + currentChunk.audioStream],
      html5:false,
      format:["wav"]});

    this.player.on("loaderror", (_id:number, err:any) => {
      console.warn("Howl load error, skipping to next chunk:", err);
      if (this.safetyTimer) clearTimeout(this.safetyTimer);
      this.playNextChunk();
    });

    this.player.on("playerror", (_id:number, err:any) => {
      console.warn("Howl play error, skipping to next chunk:", err);
      if (this.safetyTimer) clearTimeout(this.safetyTimer);
      this.playNextChunk();
    });

    this.player.on("play", () => {
      if (!this.playing)
        this.player?.pause();
    });

    let chunkAdvanced = false;
    this.player.on("end", () => {
      if (chunkAdvanced) return; // prevent double-advance
      chunkAdvanced = true;
      if (this.safetyTimer) clearTimeout(this.safetyTimer);
      this.playNextChunk();
    });

    this.player.rate(this.rate);
    this.player.play();

    // Safety timeout: if "end" event never fires, advance anyway
    // Duration is in seconds, rate affects actual time, add generous buffer
    let expectedMs = (currentChunk.duration / (this.rate || 1)) + 5000;
    this.safetyTimer = setTimeout(() => {
      if (this.playing && !chunkAdvanced)
      {
        console.warn("Safety timeout: chunk end event did not fire, advancing");
        chunkAdvanced = true;
        this.cleanupPlayer();
        this.playNextChunk();
      }
    }, expectedMs);

    // Preload next chunk in the background
    this.preloadNext();
  }



  private playNextChunk()
  {
    if (this.sentences[this.index+1])
    {
      this.nextTimer = setTimeout(() => this.playNext(), /^\s[0-9]/.test(this.sentences[this.index+1]) ? 2500 : 500) //todo: hacky to know about sentences starting with a number
    }
    else
    {
      this.background?.fade(0.2, 0, 5000);
      setTimeout(() => this.background?.stop(), 5000);
      this.playing = false;
      if (this.onPlaybackEnd)
        this.onPlaybackEnd();
    }
  }

  // Fix #2/#5: preload writes to the map by index, no push, no double-push possible
  private preloadNext()
  {
    let nextIndex = this.index + 1;
    if (nextIndex < this.sentences.length && !this.chunkMap.has(nextIndex))
    {
      // Don't start a new load if we're already loading this index
      if (this.pendingLoad && this.pendingLoad.index === nextIndex)
        return;

      let promise = this.getChunkWithRetry(this.sentences[nextIndex]);
      this.pendingLoad = {index: nextIndex, promise};

      promise.then((chunk) => {
        // Only store if this is still the pending load (not stale)
        if (this.pendingLoad && this.pendingLoad.index === nextIndex)
        {
          this.chunkMap.set(nextIndex, chunk);
          this.pendingLoad = undefined;
        }
      });
    }
  }

  // Fix #2: ensure a chunk is loaded before playing it
  private async ensureChunkLoaded(targetIndex:number):Promise<void>
  {
    if (this.chunkMap.has(targetIndex))
      return;

    // If we're already loading this exact index, wait for it
    if (this.pendingLoad && this.pendingLoad.index === targetIndex)
    {
      let chunk = await this.pendingLoad.promise;
      this.chunkMap.set(targetIndex, chunk);
      this.pendingLoad = undefined;
      return;
    }

    // Otherwise load fresh
    let chunk = await this.getChunkWithRetry(this.sentences[targetIndex]);
    this.chunkMap.set(targetIndex, chunk);
  }


  // Fix #2/#4: no more this.nextChunk! assertion, no double push
  private async playNext()
  {
    if (!this.playing)
      return;

    if (this.index < this.sentences.length - 1)
    {
      let nextIndex = this.index + 1;

      // Ensure the chunk is loaded before playing
      await this.ensureChunkLoaded(nextIndex);

      if (!this.playing) return; // could have been stopped during load

      this.index = nextIndex;
      this.playChunk();
    }
  }
}

type SpeechifyResult = {
  audioStream:string;
  format:string;
  speechMarks: {
    chunks: SpeechifyChunk[]
  }
}

export type SpeechifyChunk = {
  end:number;
  start:number;
  endTime:number;
  startTime:number;
  value:string;
} &
({
  type:"sentence";
  chunks: SpeechifyChunk[]
} |
{
  type:"word";
});

export default Reader;
